import { twMerge } from 'tailwind-merge';
import type { FeedEntry } from '~/services/layout/index.ts';
import type { Fields } from '~/services/layout/types';
import type manifest from '../config/manifest';
import { getCSSNumber, getLimit } from '~/services/layout/layout';
import type { Range } from '~/services/layout/types.ts';

export type Styles = Partial<Fields<(typeof manifest)['styles']['fields']>>;
export type Rules = Partial<Fields<(typeof manifest)['rules']['fields']>>;

export function getWidth({
  ref,
  gutter,
}: {
  ref: any;
  gutter: number | undefined;
}): number {
  if (!ref?.current) return 0;
  if (!gutter) gutter = 0;
  try {
    const width = parseInt(getComputedStyle(ref.current)?.width) + gutter;
    return width || 0;
  } catch (error) {
    return 0;
  }
}

export function getPayloadImageHeight({ ref }: any): string {
  if (!ref?.current) return 'auto';
  try {
    const maxHeight = Array.from(
      ref.current.querySelectorAll('[data-payload-image]')
    )
      .map((img: any) => img.getBoundingClientRect().height)
      .reduce(
        (maxHeight: number, currentHeight: number) =>
          Math.max(maxHeight, currentHeight),
        0
      );

    return maxHeight !== 0 ? `${maxHeight}px` : 'auto';
  } catch (error) {
    return 'auto';
  }
}

export function getCellsScrolled({
  ref,
  cellRef,
  countFrom = 'start',
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  countFrom?: 'start' | 'end';
  gutter: number | undefined;
  componentMaxWidth: number;
}): number {
  if (!ref.current) return 0;

  const cellWidth: number = getWidth({ ref: cellRef, gutter });
  const visibleCells: number = getVisibleCells({
    ref,
    cellRef,
    gutter: gutter || 0,
    componentMaxWidth,
  });
  const scrollLeft: number = getScrollLeft({ ref });
  const scrolled: number = Math.floor(scrollLeft / cellWidth);

  if (scrolled === 0 || scrolled === Infinity) return 0;

  return countFrom === 'start' ? scrolled : scrolled + visibleCells;
}

export function getToggleButtonsState({
  ref,
  circularCondition,
  nextFeedUrl,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  circularCondition?: boolean;
  nextFeedUrl?: string;
  gutter: number | undefined;
  componentMaxWidth: number;
}): {
  right: boolean;
  left: boolean;
} {
  if (!ref.current) return { right: false, left: false };

  if (circularCondition) return { right: true, left: true };

  const scrollLeft: number = getScrollLeft({ ref });
  const clientWidth: number = getClientWidth({
    ref,
    gutter,
    componentMaxWidth,
  });
  const totalScrollWidth: number = getTotalScrollWidth({ ref });

  const right: boolean = !(scrollLeft >= totalScrollWidth - clientWidth);

  return {
    right: nextFeedUrl ? true : right,
    left: scrollLeft > 0,
  };
}

export function getScrollLeft({ ref }: { ref: any }): number {
  if (!ref.current) return 0;
  return ref.current?.scrollLeft;
}

export function getTotalScrollWidth({ ref }: { ref: any }): number {
  if (!ref.current) return 0;
  return ref.current?.scrollWidth;
}

export function getTotalCells({
  entries,
  isClient,
}: {
  entries: FeedEntry[] | undefined;
  isClient: boolean | undefined;
}): number {
  let entriesLength = entries?.length || 0;

  if (
    entries?.every((obj: Object) => Object.keys(obj)?.length === 0) &&
    isClient
  ) {
    return 0;
  }

  return entriesLength;
}

export function getClientWidth({
  ref,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  gutter: number | undefined;
  componentMaxWidth: number;
}): number {
  const clientWidth = ref?.current?.clientWidth;

  if (!clientWidth) return 0;

  if (componentMaxWidth) return componentMaxWidth - (gutter || 0);

  return clientWidth;
}

export function getVisibleCells({
  ref,
  cellRef,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  gutter: number | undefined;
  componentMaxWidth: number;
}): number {
  const clientWidth: number = getClientWidth({
    ref,
    gutter,
    componentMaxWidth,
  });

  const cellWidth: number = getWidth({
    ref: cellRef,
    gutter,
  });

  const visibleCells = Math.floor(clientWidth / cellWidth);

  if (isNaN(visibleCells)) return 0;

  if (visibleCells < 0) return 0;

  return Math.floor(clientWidth / cellWidth);
}

export function getHorizontalListScrollSnapClasses(
  styles: any,
  cellScalingSwitch: boolean
): string {
  const mobileOneCell = styles?.mobile_cells_per_view < 2 && cellScalingSwitch;
  const tabletOneCell = styles?.tablet_cells_per_view < 2 && cellScalingSwitch;
  const desktopOneCell =
    styles?.desktop_cells_per_view < 2 && cellScalingSwitch;
  const largeDesktopOneCell =
    styles?.large_desktop_cells_per_view < 2 && cellScalingSwitch;

  return twMerge(
    mobileOneCell && 'mobile:max-tablet:snap-center',
    tabletOneCell && 'tablet:max-desktop:snap-center',
    desktopOneCell && 'desktop:max-large-desktop:snap-center',
    largeDesktopOneCell && 'large-desktop:snap-center'
  );
}

export function getIsAllCellsInView({
  styles,
  entriesCount,
}: {
  styles: any;
  entriesCount: number;
}) {
  const mobileCellsPerView = styles?.mobile_cells_per_view;
  const tabletCellsPerView = styles?.tablet_cells_per_view;
  const desktopCellsPerView = styles?.desktop_cells_per_view;
  const largeDesktopCellsPerView = styles?.large_desktop_cells_per_view;

  return {
    mobile: mobileCellsPerView >= entriesCount,
    tablet: tabletCellsPerView >= entriesCount,
    desktop: desktopCellsPerView >= entriesCount,
    largeDesktop: largeDesktopCellsPerView >= entriesCount,
  };
}

export function getHorizontalListButtonsVisibility({
  ref,
  cellRef,
  feedEntriesLength,
  circularCondition,
  isNextFeedLoading,
  gutter,
  isOneCellPerView,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  feedEntriesLength: number;
  circularCondition: boolean;
  isNextFeedLoading: boolean;
  gutter: number;
  isOneCellPerView: boolean;
  componentMaxWidth: number;
}): {
  showLeftButton: boolean;
  showRightButton: boolean;
} {
  const element = ref?.current;

  if (!element) return { showLeftButton: false, showRightButton: false };

  const cellsScrolled = getCellsScrolled({
    ref,
    cellRef,
    countFrom: 'end',
    gutter,
    componentMaxWidth,
  });

  const hideRightButton =
    feedEntriesLength === cellsScrolled &&
    !isNextFeedLoading &&
    !circularCondition;

  const preventShowLeftButtonBySnap: boolean =
    cellsScrolled === 0 && isOneCellPerView;

  return {
    showLeftButton:
      element.scrollLeft >= 1 &&
      !circularCondition &&
      !preventShowLeftButtonBySnap,
    showRightButton:
      Math.ceil(element.scrollLeft) <
        element.scrollWidth - element.offsetWidth && !hideRightButton,
  };
}

export function getHorizontalListButtonPosition({
  position,
  cellRef,
  ref,
}: {
  position: string;
  cellRef: any;
  ref: any;
}): string {
  if (position === 'image_center')
    return getPayloadImageHeight({ ref: cellRef });

  return (
    ref.current
      ?.querySelector('[data-horizontal-list-container]')
      ?.getBoundingClientRect()?.height || 'auto'
  );
}

export function getIsLastCell({
  ref,
  cellRef,
  feedEntriesLength,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  feedEntriesLength: number;
  gutter: number;
  componentMaxWidth: number;
}) {
  const currentLeftsideCellIndex =
    getCellsScrolled({
      ref,
      cellRef,
      countFrom: 'end',
      gutter,
      componentMaxWidth,
    }) + 1;

  const componentPaddingRight = getCSSNumber(ref.current, 'padding-right');

  const isRightMost =
    parseInt(ref.current?.scrollLeft) >=
    ref.current?.scrollWidth - window.innerWidth - componentPaddingRight;

  const isLastCell =
    currentLeftsideCellIndex >= feedEntriesLength && isRightMost;

  return isLastCell;
}

export function getIsFirstCell({
  ref,
  cellRef,
  gutter,
  componentMaxWidth,
}: {
  ref: any;
  cellRef: any;
  gutter: number;
  componentMaxWidth: number;
}) {
  const currentLeftsideCellIndex =
    getCellsScrolled({
      ref,
      cellRef,
      countFrom: 'start',
      gutter,
      componentMaxWidth,
    }) + 1;

  const componentPaddingLeft = getCSSNumber(ref.current, 'padding-left');

  const isFirstCell =
    currentLeftsideCellIndex === 1 &&
    ref.current?.scrollLeft - componentPaddingLeft <= 0;

  return isFirstCell;
}

export function getOpacity(opacity: Range<101>): number {
  if (isNaN(opacity) || opacity < 0 || opacity > 100) return 1;
  if (opacity === 0) return 0;
  const value = Math.round(opacity) / 100;
  return Math.round(value * 10) / 10;
}

export function getOffScreenOpacityLoadingClasses(styles: any): string {
  const mobile = styles?.off_screen_opacity_mobile_switch;
  const tablet = styles?.off_screen_opacity_tablet_switch;
  const desktop = styles?.off_screen_opacity_desktop_switch;
  const largeDesktop = styles?.off_screen_opacity_large_desktop_switch;

  const opacity = getOpacity(styles?.off_screen_opacity);

  return twMerge(
    mobile && `mobile:max-tablet:opacity-[${opacity}]`,
    tablet && `tablet:max-desktop:opacity-[${opacity}]`,
    desktop && `desktop:max-large-desktop:opacity-[${opacity}]`,
    largeDesktop && `large-desktop:opacity-[${opacity}]`
  );
}

export function getIndicatorsEntries({
  feed,
  itemLimit,
}: {
  feed: any;
  itemLimit: number;
}): FeedEntry[] {
  return feed?.entry?.slice(0, getLimit(itemLimit));
}

export function getHorizontalListMaxWidthClasses({
  styles,
  defaultThemeV2Config,
}: {
  styles: Styles;
  defaultThemeV2Config: any;
}): string {
  const { mobileSwitch, tabletSwitch, desktopSwitch, largeDesktopSwitch } =
    getHorizontalListMaxWidthSwitches({
      styles,
      defaultThemeV2Config,
    });

  return twMerge(
    mobileSwitch && 'mobile:max-tablet:horizontal-list-padding',
    mobileSwitch && 'mobile:max-tablet:horizontal-list-maximum-width',

    tabletSwitch && 'tablet:max-desktop:horizontal-list-padding',
    tabletSwitch && 'tablet:max-desktop:horizontal-list-maximum-width',

    desktopSwitch && 'desktop:max-large-desktop:horizontal-list-padding',
    desktopSwitch && 'desktop:max-large-desktop:horizontal-list-maximum-width',

    largeDesktopSwitch && 'large-desktop:horizontal-list-padding',
    largeDesktopSwitch && 'large-desktop:horizontal-list-maximum-width'
  );
}

export function getHorizontalListMaxWidthRightPadding({
  styles,
  defaultThemeV2Config,
}: {
  styles: Styles;
  defaultThemeV2Config: any;
}): string {
  const { mobileSwitch, tabletSwitch, desktopSwitch, largeDesktopSwitch } =
    getHorizontalListMaxWidthSwitches({
      styles,
      defaultThemeV2Config,
    });

  return twMerge(
    mobileSwitch &&
      'mobile:max-tablet:horizontal-list-maximum-width-right-padding',
    tabletSwitch &&
      'tablet:max-desktop:horizontal-list-maximum-width-right-padding',
    desktopSwitch &&
      'desktop:max-large-desktop:horizontal-list-maximum-width-right-padding',
    largeDesktopSwitch &&
      'large-desktop:horizontal-list-maximum-width-right-padding'
  );
}

export function getHorizontalListButtonMaxWidthClasses({
  styles,
  defaultThemeV2Config,
  direction,
}: {
  styles: Styles;
  defaultThemeV2Config: any;
  direction: 'left' | 'right';
}): string {
  const { mobileSwitch, tabletSwitch, desktopSwitch, largeDesktopSwitch } =
    getHorizontalListMaxWidthSwitches({
      styles,
      defaultThemeV2Config,
    });

  return twMerge(
    mobileSwitch &&
      direction === 'right' &&
      'mobile:max-tablet:horizontal-list-right-button-maximum-width',
    tabletSwitch &&
      direction === 'right' &&
      'tablet:max-desktop:horizontal-list-right-button-maximum-width',
    desktopSwitch &&
      direction === 'right' &&
      'desktop:max-large-desktop:horizontal-list-right-button-maximum-width',
    largeDesktopSwitch &&
      direction === 'right' &&
      'large-desktop:horizontal-list-right-button-maximum-width',

    mobileSwitch &&
      direction === 'left' &&
      'mobile:max-tablet:horizontal-list-left-button-maximum-width',
    tabletSwitch &&
      direction === 'left' &&
      'tablet:max-desktop:horizontal-list-left-button-maximum-width',
    desktopSwitch &&
      direction === 'left' &&
      'desktop:max-large-desktop:horizontal-list-left-button-maximum-width',
    largeDesktopSwitch &&
      direction === 'left' &&
      'large-desktop:horizontal-list-left-button-maximum-width'
  );
}

function getHorizontalListMaxWidthSwitches({
  styles,
  defaultThemeV2Config,
}: {
  styles: Styles;
  defaultThemeV2Config: any;
}) {
  const mobileMaximumWidth =
    styles?.component_mobile_maximum_width ||
    defaultThemeV2Config?.component_mobile_maximum_width;
  const tabletMaximumWidth =
    styles?.component_tablet_maximum_width ||
    defaultThemeV2Config?.component_tablet_maximum_width;
  const desktopMaximumWidth =
    styles?.component_desktop_maximum_width ||
    defaultThemeV2Config?.component_desktop_maximum_width;
  const largeDesktopMaximumWidth =
    styles?.component_large_desktop_maximum_width ||
    defaultThemeV2Config?.component_large_desktop_maximum_width;

  const mobileSwitch: boolean =
    styles?.component_mobile_maximum_width_switch && mobileMaximumWidth;
  const tabletSwitch: boolean =
    styles?.component_tablet_maximum_width_switch && tabletMaximumWidth;
  const desktopSwitch: boolean =
    styles?.component_desktop_maximum_width_switch && desktopMaximumWidth;
  const largeDesktopSwitch: boolean =
    styles?.component_large_desktop_maximum_width_switch &&
    largeDesktopMaximumWidth;

  return {
    mobileSwitch,
    tabletSwitch,
    desktopSwitch,
    largeDesktopSwitch,
  };
}

export function getHorizontalListPadding({
  styles,
  defaultThemeV2Config,
}: {
  styles: Styles;
  defaultThemeV2Config: any;
}) {
  const { mobileSwitch, tabletSwitch, desktopSwitch, largeDesktopSwitch } =
    getHorizontalListMaxWidthSwitches({
      styles,
      defaultThemeV2Config,
    });

  return twMerge(
    !mobileSwitch && 'mobile:max-tablet:horizontal-list-padding',
    !tabletSwitch && 'tablet:max-desktop:horizontal-list-padding',
    !desktopSwitch && 'desktop:max-large-desktop:horizontal-list-padding',
    !largeDesktopSwitch && 'large-desktop:horizontal-list-padding'
  );
}

export function getHorizontalListScrollPx({
  gutter,
  cellsPerView,
  nextCellOffset,
  paddingLeft,
  componentMaxWidth,
  ref,
}: {
  gutter: number;
  cellsPerView: number;
  nextCellOffset: number;
  paddingLeft: number;
  componentMaxWidth: number;
  ref: any;
}) {
  const isSpacingEqual: boolean =
    gutter * cellsPerView + nextCellOffset - gutter ===
    paddingLeft + nextCellOffset;

  if (!componentMaxWidth) {
    const clientWidth = ref?.current?.clientWidth ?? 0;
    if (isSpacingEqual) return clientWidth - paddingLeft;
    return Math.max(0, clientWidth - paddingLeft - nextCellOffset);
  }

  if (isSpacingEqual) return componentMaxWidth - paddingLeft - nextCellOffset;

  if (!nextCellOffset) return componentMaxWidth - paddingLeft;

  return componentMaxWidth - paddingLeft - gutter;
}

/**
 * Determines if horizontal list should scroll one item at a time.
 * Returns true when itemsPerView is not configured AND fixed item size exceeds 55% of screen width.
 * @param itemsPerView - Number of items visible per view (undefined or 0 means not configured).
 * @param fixedItemSize - Fixed size of items in pixels.
 * @param screenWidth - Current screen width in pixels.
 * @returns True if should scroll one item at a time, false otherwise.
 */
export function shouldScrollOneItem({
  itemsPerView,
  fixedItemSize,
  screenWidth,
}: {
  itemsPerView: number | undefined;
  fixedItemSize: number | undefined;
  screenWidth: number;
}): boolean {
  if (
    (!itemsPerView || itemsPerView === 0) &&
    fixedItemSize &&
    screenWidth > 0
  ) {
    const percentageOfScreen = (fixedItemSize / screenWidth) * 100;
    if (percentageOfScreen > 55) {
      return true;
    }
  }

  return false;
}

/**
 * Determines the scrolling strategy based on responsive configuration.
 * @param itemsPerView - Number of items visible per view.
 * @param fixedItemSize - Fixed size of items in pixels.
 * @param screenWidth - Current screen width in pixels.
 * @returns 'scrollByOne' if should scroll one item at a time, 'scrollByPage' otherwise.
 */
export function getScrollingStrategy({
  itemsPerView,
  fixedItemSize,
  screenWidth,
}: {
  itemsPerView: number | undefined;
  fixedItemSize: number | undefined;
  screenWidth: number;
}): 'scrollByOne' | 'scrollByPage' {
  const scrollOne = shouldScrollOneItem({
    itemsPerView,
    fixedItemSize,
    screenWidth,
  });

  return scrollOne ? 'scrollByOne' : 'scrollByPage';
}

/**
 * Calculates the initial scroll offset to position at the first duplicated entry
 * with the previous item visible via nextCellOffset peek.
 * @param cellWidth - Width of a single cell including gutter.
 * @param nextCellOffset - Offset to show peek of next/previous cell.
 * @param originalEntriesCount - Number of original entries (before duplication).
 * @param paddingLeft - Left padding of the scroll container.
 * @returns Scroll offset in pixels, or 0 if invalid parameters.
 */
export function getInitialScrollOffset({
  cellWidth,
  nextCellOffset,
  originalEntriesCount,
  marginRight,
}: {
  cellWidth: number;
  nextCellOffset: number;
  originalEntriesCount: number;
  marginRight: number;
}): number {
  if (
    !cellWidth ||
    cellWidth <= 0 ||
    !originalEntriesCount ||
    originalEntriesCount <= 0
  ) {
    return 0;
  }

  const totalWidthToFirstDuplicate = cellWidth * originalEntriesCount;
  const offset = totalWidthToFirstDuplicate + marginRight + nextCellOffset;

  return Math.max(0, offset);
}
